"use strict";
import { BigNumber } from "@ethersproject/bignumber";
import { isFewToken } from "@ring-protocol/few-v2-sdk";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { CurrencyAmount, Token, TradeType } from "@uniswap/sdk-core";
import UniswapXBolt from "assets/svg/bolt.svg";
import StaticRouteIcon from "assets/svg/static_route.svg";
import { getCurrency } from "components/AccountDrawer/MiniPortfolio/Activity/getCurrency";
import { getBridgeDescriptor } from "components/AccountDrawer/MiniPortfolio/Activity/parseRemote";
import {
  CancelledTransactionTitleTable,
  LimitOrderTextTable,
  OrderTextTable,
  getActivityTitle
} from "components/AccountDrawer/MiniPortfolio/constants";
import { MINT_CONFIG } from "pages/Mint/MintConfig";
import { isOnChainOrder, useAllSignatures } from "state/signatures/hooks";
import { SignatureType } from "state/signatures/types";
import { useMultichainTransactions } from "state/transactions/hooks";
import {
  TransactionType
} from "state/transactions/types";
import { isConfirmedTx } from "state/transactions/utils";
import { nativeOnChain } from "uniswap/src/constants/tokens";
import { TransactionStatus } from "uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { useEnabledChains } from "uniswap/src/features/chains/hooks/useEnabledChains";
import i18n from "uniswap/src/i18n";
import { currencyIdToAddress } from "uniswap/src/utils/currencyId";
import { isAddress, isSameAddress } from "utilities/src/addresses";
import { logger } from "utilities/src/logger/logger";
import { ReactQueryCacheKey } from "utilities/src/reactQuery/cache";
import { NumberType, useFormatter } from "utils/formatNumbers";
function buildCurrencyDescriptor(currencyA, amtA, currencyB, amtB, formatNumber, isSwap = false) {
  const formattedA = currencyA ? formatNumber({
    input: parseFloat(CurrencyAmount.fromRawAmount(currencyA, amtA).toSignificant()),
    type: NumberType.TokenNonTx
  }) : i18n.t("common.unknown");
  const symbolA = currencyA?.symbol ? ` ${currencyA?.symbol}` : "";
  const formattedB = currencyB ? formatNumber({
    input: parseFloat(CurrencyAmount.fromRawAmount(currencyB, amtB).toSignificant()),
    type: NumberType.TokenNonTx
  }) : i18n.t("common.unknown");
  const symbolB = currencyB?.symbol ? ` ${currencyB?.symbol}` : "";
  const amountWithSymbolA = `${formattedA}${symbolA}`;
  const amountWithSymbolB = `${formattedB}${symbolB}`;
  return isSwap ? i18n.t("activity.transaction.swap.descriptor", {
    amountWithSymbolA,
    amountWithSymbolB
  }) : i18n.t("activity.transaction.tokens.descriptor", {
    amountWithSymbolA,
    amountWithSymbolB
  });
}
async function parseSwap(swap, chainId, formatNumber) {
  const tokenInFallback = getConfiguredMintCurrencyById(swap.inputCurrencyId, chainId);
  const tokenOutFallback = getConfiguredMintCurrencyById(swap.outputCurrencyId, chainId);
  const [tokenIn, tokenOut] = await Promise.all([
    getActivityCurrencyById(
      swap.inputCurrencyId,
      chainId,
      tokenInFallback?.decimals,
      tokenInFallback?.symbol,
      tokenInFallback?.name
    ),
    getActivityCurrencyById(
      swap.outputCurrencyId,
      chainId,
      tokenOutFallback?.decimals,
      tokenOutFallback?.symbol,
      tokenOutFallback?.name
    )
  ]);
  const [inputRaw, outputRaw] = swap.tradeType === TradeType.EXACT_INPUT ? [swap.inputCurrencyAmountRaw, swap.settledOutputCurrencyAmountRaw ?? swap.expectedOutputCurrencyAmountRaw] : [swap.expectedInputCurrencyAmountRaw, swap.outputCurrencyAmountRaw];
  return {
    descriptor: buildCurrencyDescriptor(tokenIn, inputRaw, tokenOut, outputRaw, formatNumber, true),
    currencies: [tokenIn, tokenOut],
    prefixIconSrc: swap.isUniswapXOrder ? UniswapXBolt : void 0
  };
}
async function parseMint(mint, chainId, formatNumber) {
  const [tokenIn, tokenOut] = await Promise.all([
    getActivityCurrencyById(
      mint.inputCurrencyId,
      chainId,
      mint.inputCurrencyDecimals,
      mint.inputCurrencySymbol,
      mint.inputCurrencyName
    ),
    getActivityCurrencyById(
      mint.outputCurrencyId,
      chainId,
      mint.outputCurrencyDecimals,
      mint.outputCurrencySymbol,
      mint.outputCurrencyName
    )
  ]);
  return {
    descriptor: buildCurrencyDescriptor(
      tokenIn,
      mint.inputCurrencyAmountRaw,
      tokenOut,
      mint.outputCurrencyAmountRaw,
      formatNumber,
      true
    ),
    currencies: [tokenIn, tokenOut]
  };
}
async function parseBridge(bridge, inputChainId, outputChainId, formatNumber) {
  const [tokenIn, tokenOut] = await Promise.all([
    getCurrency(bridge.inputCurrencyId, inputChainId),
    getCurrency(bridge.outputCurrencyId, outputChainId)
  ]);
  const inputAmount = tokenIn ? formatNumber({
    input: parseFloat(CurrencyAmount.fromRawAmount(tokenIn, bridge.inputCurrencyAmountRaw).toSignificant()),
    type: NumberType.TokenNonTx
  }) : i18n.t("common.unknown");
  const outputAmount = tokenOut ? formatNumber({
    input: parseFloat(CurrencyAmount.fromRawAmount(tokenOut, bridge.outputCurrencyAmountRaw).toSignificant()),
    type: NumberType.TokenNonTx
  }) : i18n.t("common.unknown");
  return {
    descriptor: getBridgeDescriptor({ tokenIn, tokenOut, inputAmount, outputAmount }),
    chainId: inputChainId,
    outputChainId,
    currencies: [tokenIn, tokenOut],
    prefixIconSrc: void 0
  };
}
async function parseFewWrap(wrap, chainId, status, formatNumber) {
  const native = nativeOnChain(chainId);
  const wrapped = native.wrapped;
  let input;
  let output;
  if (wrap.inputCurrencyId) {
    input = await getCurrency(currencyIdToAddress(wrap.inputCurrencyId), chainId);
  }
  if (wrap.outputCurrencyId) {
    output = await getCurrency(currencyIdToAddress(wrap.outputCurrencyId), chainId);
  }
  if (!input) {
    input = wrap.unwrapped ? wrapped : native;
  }
  if (!output) {
    output = wrap.unwrapped ? native : wrapped;
  }
  let inputForDisplay = input;
  let outputForDisplay = output;
  if (input && output && input.isToken && output.isToken) {
    const inputToken = input;
    const outputToken = output;
    const isInputFewToken = isFewToken(inputToken);
    const isOutputFewToken = isFewToken(outputToken);
    if (isInputFewToken && !isOutputFewToken) {
      inputForDisplay = new Token(
        inputToken.chainId,
        inputToken.address,
        outputToken.decimals,
        inputToken.symbol,
        inputToken.name
      );
    }
    if (!isInputFewToken && isOutputFewToken) {
      outputForDisplay = new Token(
        outputToken.chainId,
        outputToken.address,
        inputToken.decimals,
        outputToken.symbol,
        outputToken.name
      );
    }
  }
  const descriptor = buildCurrencyDescriptor(
    inputForDisplay,
    wrap.currencyAmountRaw,
    outputForDisplay,
    wrap.currencyAmountRaw,
    formatNumber,
    true
  );
  const title = getActivityTitle(TransactionType.WRAP, status, wrap.unwrapped);
  const currencies = [input, output];
  return { title, descriptor, currencies };
}
function parseWrap(wrap, chainId, status, formatNumber) {
  const native = nativeOnChain(chainId);
  const wrapped = native.wrapped;
  const [input, output] = wrap.unwrapped ? [wrapped, native] : [native, wrapped];
  const descriptor = buildCurrencyDescriptor(
    input,
    wrap.currencyAmountRaw,
    output,
    wrap.currencyAmountRaw,
    formatNumber,
    true
  );
  const title = getActivityTitle(TransactionType.WRAP, status, wrap.unwrapped);
  const currencies = wrap.unwrapped ? [wrapped, native] : [native, wrapped];
  return { title, descriptor, currencies };
}
function createFallbackToken(address, chainId, decimals, symbol, name) {
  if (decimals === void 0 || !symbol) {
    return void 0;
  }
  return new Token(chainId, address, decimals, symbol, name ?? symbol);
}
async function getActivityCurrency(address, chainId, decimals, symbol, name) {
  return await getCurrency(address, chainId) ?? createFallbackToken(address, chainId, decimals, symbol, name);
}
function currencyAddressFromId(currencyId) {
  try {
    return currencyIdToAddress(currencyId);
  } catch {
    return currencyId;
  }
}
function getConfiguredMintCurrencyById(currencyId, chainId) {
  if (MINT_CONFIG.chainId !== chainId) {
    return void 0;
  }
  const address = currencyAddressFromId(currencyId);
  return [MINT_CONFIG.usdr, ...MINT_CONFIG.stables].find((token) => token && isSameAddress(token.address, address));
}
function isLegacyUsdrMintSwap(info, chainId) {
  if (info.type !== TransactionType.SWAP) {
    return false;
  }
  const inputCurrency = getConfiguredMintCurrencyById(info.inputCurrencyId, chainId);
  const outputCurrency = getConfiguredMintCurrencyById(info.outputCurrencyId, chainId);
  return Boolean(inputCurrency && outputCurrency && inputCurrency.symbol !== "USDR" && outputCurrency.symbol === "USDR");
}
function getDisplayTransactionType(info, chainId) {
  return isLegacyUsdrMintSwap(info, chainId) ? TransactionType.MINT : info.type;
}
async function getActivityCurrencyById(currencyId, chainId, decimals, symbol, name) {
  return getActivityCurrency(currencyAddressFromId(currencyId), chainId, decimals, symbol, name);
}
function formatAmountWithSymbol(amountRaw, currency, formatNumber) {
  if (!currency) {
    return i18n.t("common.unknown");
  }
  const formattedAmount = formatNumber({
    input: parseFloat(CurrencyAmount.fromRawAmount(currency, amountRaw).toSignificant()),
    type: NumberType.TokenNonTx
  });
  return currency.symbol ? `${formattedAmount} ${currency.symbol}` : formattedAmount;
}
async function parseApproval(approval, chainId, status) {
  const currency = await getActivityCurrency(
    approval.tokenAddress,
    chainId,
    approval.tokenDecimals,
    approval.tokenSymbol,
    approval.tokenName
  );
  const descriptor = currency?.symbol ?? currency?.name ?? i18n.t("common.unknown");
  return {
    title: getActivityTitle(
      TransactionType.APPROVAL,
      status,
      BigNumber.from(approval.amount).eq(0)
    ),
    descriptor,
    currencies: [currency]
  };
}
async function parseMorphoMarket(info, chainId, status, formatNumber) {
  const currency = await getActivityCurrency(
    info.assetAddress,
    chainId,
    info.assetDecimals,
    info.assetSymbol,
    info.assetSymbol
  );
  const amountWithSymbol = formatAmountWithSymbol(info.assetAmountRaw, currency, formatNumber);
  return {
    title: getActivityTitle(info.type, status),
    descriptor: i18n.t("activity.transaction.lending.market.descriptor", {
      amountWithSymbol,
      marketLabel: info.marketTitle
    }),
    currencies: [currency]
  };
}
async function parseMorphoVault(info, chainId, status, formatNumber) {
  const currency = await getActivityCurrency(
    info.assetAddress,
    chainId,
    info.assetDecimals,
    info.assetSymbol,
    info.assetSymbol
  );
  const amountWithSymbol = formatAmountWithSymbol(info.assetAmountRaw, currency, formatNumber);
  return {
    title: getActivityTitle(info.type, status),
    descriptor: i18n.t("activity.transaction.lending.vault.descriptor", {
      amountWithSymbol,
      vaultLabel: info.vaultTitle
    }),
    currencies: [currency]
  };
}
async function parseMorphoVaultRedeem(info, chainId, status, formatNumber) {
  const shareCurrency = createFallbackToken(info.vaultAddress, chainId, info.shareDecimals, i18n.t("common.shares"), info.vaultTitle) ?? void 0;
  const assetCurrency = await getActivityCurrency(
    info.assetAddress,
    chainId,
    void 0,
    info.assetSymbol,
    info.assetSymbol
  );
  const formattedShares = shareCurrency ? formatNumber({
    input: parseFloat(CurrencyAmount.fromRawAmount(shareCurrency, info.shareAmountRaw).toSignificant()),
    type: NumberType.TokenNonTx
  }) : i18n.t("common.unknown");
  return {
    title: getActivityTitle(info.type, status),
    descriptor: i18n.t("activity.transaction.lending.redeem.descriptor", {
      amountWithSymbol: `${formattedShares} ${i18n.t("common.shares")}`,
      vaultLabel: info.vaultTitle
    }),
    currencies: [assetCurrency]
  };
}
function parseMorphoAuthorization(info, status) {
  return {
    title: getActivityTitle(info.type, status),
    descriptor: i18n.t("activity.transaction.lending.authorization.descriptor", {
      label: info.protocolLabel
    })
  };
}
async function parseLegacyLP(lp, chainId, formatNumber) {
  const [baseCurrency, quoteCurrency] = await Promise.all([
    getCurrency(lp.baseCurrencyId, chainId),
    getCurrency(lp.quoteCurrencyId, chainId)
  ]);
  const [baseRaw, quoteRaw] = [lp.expectedAmountBaseRaw, lp.expectedAmountQuoteRaw];
  const descriptor = buildCurrencyDescriptor(baseCurrency, baseRaw, quoteCurrency, quoteRaw, formatNumber);
  return { descriptor, currencies: [baseCurrency, quoteCurrency] };
}
async function parseLiquidity(lp, chainId, formatNumber) {
  const [token0Currency, token1Currency] = await Promise.all([
    getCurrency(lp.token0CurrencyId, chainId),
    getCurrency(lp.token1CurrencyId, chainId)
  ]);
  const [token0Raw, token1Raw] = [lp.token0CurrencyAmountRaw, lp.token1CurrencyAmountRaw];
  const descriptor = buildCurrencyDescriptor(token0Currency, token0Raw, token1Currency, token1Raw, formatNumber);
  return { descriptor, currencies: [token0Currency, token1Currency] };
}
async function parseCollectFees(collect, chainId, formatNumber) {
  const {
    token0CurrencyId: baseCurrencyId,
    token1CurrencyId: quoteCurrencyId,
    token0CurrencyAmountRaw: expectedAmountBaseRaw,
    token1CurrencyAmountRaw: expectedAmountQuoteRaw
  } = collect;
  return parseLegacyLP(
    { baseCurrencyId, quoteCurrencyId, expectedAmountBaseRaw, expectedAmountQuoteRaw },
    chainId,
    formatNumber
  );
}
async function parseMigrateCreateV3(lp, chainId) {
  const [baseCurrency, quoteCurrency] = await Promise.all([
    getCurrency(lp.baseCurrencyId, chainId),
    getCurrency(lp.quoteCurrencyId, chainId)
  ]);
  const baseSymbol = baseCurrency?.symbol ?? i18n.t("common.unknown");
  const quoteSymbol = quoteCurrency?.symbol ?? i18n.t("common.unknown");
  const descriptor = i18n.t("activity.transaction.tokens.descriptor", {
    amountWithSymbolA: baseSymbol,
    amountWithSymbolB: quoteSymbol
  });
  return { descriptor, currencies: [baseCurrency, quoteCurrency] };
}
async function parseSend(send, chainId, formatNumber) {
  const { currencyId, amount, recipient } = send;
  const currency = await getCurrency(currencyId, chainId);
  const formattedAmount = currency ? formatNumber({
    input: parseFloat(CurrencyAmount.fromRawAmount(currency, amount).toSignificant()),
    type: NumberType.TokenNonTx
  }) : i18n.t("common.unknown");
  const otherAccount = isAddress(recipient) || void 0;
  return {
    descriptor: i18n.t("activity.transaction.send.descriptor", {
      amountWithSymbol: `${formattedAmount} ${currency?.symbol}`,
      walletAddress: recipient
    }),
    otherAccount,
    currencies: [currency]
  };
}
async function parseLpIncentivesClaim(info, chainId) {
  const token = await getCurrency(info.tokenAddress, chainId);
  const symbol = token?.symbol ?? i18n.t("common.unknown");
  return {
    descriptor: i18n.t("activity.transaction.lpRewards.descriptor", { symbol }),
    currencies: [token]
  };
}
export async function transactionToActivity(details, chainId, formatNumber) {
  if (!details) {
    return void 0;
  }
  try {
    const displayType = getDisplayTransactionType(details.info, chainId);
    const defaultFields = {
      hash: details.hash,
      chainId,
      title: getActivityTitle(displayType, details.status),
      status: details.status,
      timestamp: (isConfirmedTx(details) ? details.confirmedTime : details.addedTime) / 1e3,
      from: details.from,
      nonce: details.nonce,
      cancelled: details.cancelled
    };
    let additionalFields = {};
    const info = details.info;
    if (info.type === TransactionType.SWAP) {
      additionalFields = await parseSwap(info, chainId, formatNumber);
    } else if (info.type === TransactionType.MINT) {
      additionalFields = await parseMint(info, chainId, formatNumber);
    } else if (info.type === TransactionType.BRIDGE) {
      additionalFields = await parseBridge(info, chainId, info.outputChainId ?? chainId, formatNumber);
    } else if (info.type === TransactionType.APPROVAL) {
      additionalFields = await parseApproval(info, chainId, details.status);
    } else if (info.type === TransactionType.MORPHO_AUTHORIZATION) {
      additionalFields = parseMorphoAuthorization(info, details.status);
    } else if (info.type === TransactionType.MORPHO_SUPPLY || info.type === TransactionType.BORROW || info.type === TransactionType.REPAY || info.type === TransactionType.WITHDRAW_LIQUIDITY_STAKING && "protocol" in info && info.protocol === "morpho-market") {
      additionalFields = await parseMorphoMarket(
        info,
        chainId,
        details.status,
        formatNumber
      );
    } else if (info.type === TransactionType.MORPHO_REDEEM || info.type === TransactionType.DEPOSIT_LIQUIDITY_STAKING && "protocol" in info && info.protocol === "morpho-vault" || info.type === TransactionType.WITHDRAW_LIQUIDITY_STAKING && "protocol" in info && info.protocol === "morpho-vault") {
      additionalFields = info.type === TransactionType.MORPHO_REDEEM ? await parseMorphoVaultRedeem(info, chainId, details.status, formatNumber) : await parseMorphoVault(
        info,
        chainId,
        details.status,
        formatNumber
      );
    } else if (info.type === TransactionType.WRAP) {
      if (info.inputCurrencyId && info.inputCurrencyId) {
        additionalFields = await parseFewWrap(info, chainId, details.status, formatNumber);
      } else {
        additionalFields = parseWrap(info, chainId, details.status, formatNumber);
      }
    } else if (info.type === TransactionType.ADD_LIQUIDITY_V3_POOL || info.type === TransactionType.REMOVE_LIQUIDITY_V3 || info.type === TransactionType.ADD_LIQUIDITY_V2_POOL) {
      additionalFields = await parseLegacyLP(info, chainId, formatNumber);
    } else if (info.type === TransactionType.INCREASE_LIQUIDITY || info.type === TransactionType.DECREASE_LIQUIDITY || info.type === TransactionType.CREATE_POSITION || info.type === TransactionType.MIGRATE_LIQUIDITY_V3_TO_V4) {
      additionalFields = await parseLiquidity(info, chainId, formatNumber);
    } else if (info.type === TransactionType.COLLECT_FEES) {
      additionalFields = await parseCollectFees(info, chainId, formatNumber);
    } else if (info.type === TransactionType.MIGRATE_LIQUIDITY_V2_TO_V3 || info.type === TransactionType.CREATE_V3_POOL) {
      additionalFields = await parseMigrateCreateV3(info, chainId);
    } else if (info.type === TransactionType.SEND) {
      additionalFields = await parseSend(info, chainId, formatNumber);
    } else if (info.type === TransactionType.LP_INCENTIVES_CLAIM_REWARDS) {
      additionalFields = await parseLpIncentivesClaim(info, chainId);
    } else if (info.type === TransactionType.PERMIT) {
      additionalFields = {
        title: i18n.t("common.permit"),
        descriptor: i18n.t("notification.transaction.unknown.success.short"),
        logos: [StaticRouteIcon]
      };
    }
    const activity = { ...defaultFields, ...additionalFields };
    if (details.cancelled) {
      activity.title = CancelledTransactionTitleTable[displayType];
      activity.status = TransactionStatus.Confirmed;
    }
    return activity;
  } catch (error) {
    logger.warn("parseLocal", "transactionToActivity", `Failed to parse transaction ${details.hash}`, error);
    return void 0;
  }
}
export function getTransactionToActivityQueryOptions(transaction, chainId, formatNumber) {
  return queryOptions({
    queryKey: [ReactQueryCacheKey.TransactionToActivity, transaction, chainId],
    queryFn: async () => transactionToActivity(transaction, chainId, formatNumber)
  });
}
export function getSignatureToActivityQueryOptions(signature, formatNumber) {
  return queryOptions({
    queryKey: [ReactQueryCacheKey.SignatureToActivity, signature],
    queryFn: async () => signatureToActivity(signature, formatNumber)
  });
}
function convertToSecTimestamp(timestamp) {
  const threshold = 41024448e5;
  if (timestamp >= threshold) {
    return Math.floor(timestamp / 1e3);
  } else {
    return timestamp;
  }
}
export async function signatureToActivity(signature, formatNumber) {
  if (!signature) {
    return void 0;
  }
  switch (signature.type) {
    case SignatureType.SIGN_UNISWAPX_ORDER:
    case SignatureType.SIGN_UNISWAPX_V2_ORDER:
    case SignatureType.SIGN_UNISWAPX_V3_ORDER:
    case SignatureType.SIGN_PRIORITY_ORDER:
    case SignatureType.SIGN_LIMIT: {
      if (isOnChainOrder(signature.status)) {
        return void 0;
      }
      const { title, statusMessage, status } = signature.type === SignatureType.SIGN_LIMIT ? LimitOrderTextTable[signature.status] : OrderTextTable[signature.status];
      return {
        hash: signature.orderHash,
        chainId: signature.chainId,
        title,
        status,
        offchainOrderDetails: signature,
        timestamp: convertToSecTimestamp(signature.addedTime),
        from: signature.offerer,
        statusMessage,
        prefixIconSrc: UniswapXBolt,
        ...await parseSwap(signature.swapInfo, signature.chainId, formatNumber)
      };
    }
    default:
      return void 0;
  }
}
export function useLocalActivities(account) {
  const allTransactions = useMultichainTransactions();
  const allSignatures = useAllSignatures();
  const { formatNumber } = useFormatter();
  const { chains } = useEnabledChains();
  const { data } = useQuery({
    queryKey: [ReactQueryCacheKey.LocalActivities, account, allTransactions, allSignatures],
    queryFn: async () => {
      const transactions = Object.values(allTransactions).filter(([transaction]) => transaction.from === account).filter(([, chainId]) => chains.includes(chainId)).map(([transaction, chainId]) => transactionToActivity(transaction, chainId, formatNumber));
      const signatures = Object.values(allSignatures).filter((signature) => signature.offerer === account).map((signature) => signatureToActivity(signature, formatNumber));
      return (await Promise.all([...transactions, ...signatures])).reduce((acc, activity) => {
        if (activity) {
          acc[activity.hash] = activity;
        }
        return acc;
      }, {});
    }
  });
  return data ?? {};
}
